import { useState } from "react";
import {
	ListItem,
	Typography,
	Box,
	Popover,
	Button,
	Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "@emotion/react";
import { InsertEmoticon, Loop } from "@mui/icons-material";
import {
	useEditSubcategoryMutation,
	useSaveBudgetMutation,
} from "../../../services/queryHooks";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import AddOrEditBudgetModal from "./AddOrEditBudgetModal";

export default function SubcategoryLine({
	subcategory,
	type,
	categoryGroupId,
	period,
	categoryGroupName,
	maxAmount,
}) {
	const { emoji, name, translationKey, budget, transactionsSum } =
		subcategory;
	const { t } = useTranslation();
	const theme = useTheme();

	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedIndex, setSelectedIndex] = useState(null);
	const [isAddOrEditModalOpen, setIsAddOrEditModalOpen] = useState(false);

	const editSubcategoryMutation = useEditSubcategoryMutation();
	const { mutate: saveBudget, isPending } = useSaveBudgetMutation();

	const isNoSubcategory = translationKey === "noSubcategory";

	const userCurrency = localStorage.getItem("userCurrency");

	const handleClick = (event, id) => {
		if (id.includes("noSub")) {
			return;
		}
		setAnchorEl(event.currentTarget);
		setSelectedIndex(id);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleEmojiSelect = (emoji) => {
		editSubcategoryMutation.mutate({
			categoryGroupId,
			subcategoryId: selectedIndex,
			emoji: emoji.native,
		});
		setSelectedIndex(null);
		handleClose();
	};

	const handleSubmit = (data) => {
		saveBudget(data);
	};

	const balanceStyles = {
		color: (
			type === "expense"
				? transactionsSum <= budget
				: transactionsSum >= budget
		)
			? theme.palette.values.positive
			: theme.palette.values.negative,
	};

	const boxStyles = {
		cursor: subcategory._id.includes("noSub") ? "auto" : "pointer",
		minWidth: "auto",
		color: "inherit",
		display: "inline-block",
		verticalAlign: "bottom",
	};

	return (
		<>
			<ListItem
				key={subcategory._id}
				sx={{
					pl: 5,
					py: 0,
					display: "flex",
					justifyContent: "space-between",
				}}
			>
				<Box sx={{ display: "flex", gap: 0.5 }}>
					<Box
						sx={boxStyles}
						onClick={(e) => handleClick(e, subcategory._id)}
					>
						{emoji ? (
							<span
								style={{
									color: "null",
								}}
							>
								{emoji}
							</span>
						) : (
							<Box sx={{ width: "23.7px" }}>
								<span>
									<InsertEmoticon
										sx={{
											width: "22px",
											verticalAlign: "bottom",
										}}
									/>
								</span>
							</Box>
						)}
					</Box>{" "}
					<Typography variant="body1">
						{name ? name : t(translationKey)}
					</Typography>
				</Box>
				<Box sx={{ display: "flex" }}>
					<Typography
						variant="body1"
						sx={{
							...balanceStyles,
						}}
					>
						{Math.abs(transactionsSum).toFixed(2).toLocaleString()}{" "}
						{userCurrency}
					</Typography>
					<Typography
						variant="body1"
						sx={{
							ml: 0.5,
						}}
					>
						{"/"}
					</Typography>
					<Button
						color="primary"
						variant="text"
						onClick={() => setIsAddOrEditModalOpen(true)}
						sx={{ py: 0, px: 0.5, fontWeight: "normal" }}
					>
						{subcategory.isBudgetRecurring ? (
							<Tooltip
								title={t("recurringBudget")}
								placement="top"
								arrow
							>
								<span>
									<Loop
										sx={{
											width: "19px",
											verticalAlign: "sub",
										}}
									/>
								</span>
							</Tooltip>
						) : null}{" "}
						<Typography variant="body1">
							{Math.abs(budget).toFixed(2).toLocaleString()}{" "}
							{userCurrency}
						</Typography>
					</Button>
				</Box>
			</ListItem>
			<Popover
				disableEnforceFocus
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
			>
				<Picker
					data={data}
					onEmojiSelect={handleEmojiSelect}
					previewPosition="none"
				/>
			</Popover>
			<AddOrEditBudgetModal
				isOpen={isAddOrEditModalOpen}
				onClose={() => setIsAddOrEditModalOpen(false)}
				initialData={{
					budgetId:
						subcategory.budgetSource === "currentPeriod"
							? subcategory.budgetId
							: "",
					categoryId: isNoSubcategory ? "" : subcategory._id,
					categoryName: isNoSubcategory
						? ""
						: subcategory.name
							? subcategory.name
							: t(subcategory.translationKey),
					categoryGroupId: categoryGroupId,
					categoryGroupName: categoryGroupName,
					date: period,
					amount: subcategory.budget,
					minAmount: 0,
					maxAmount,
					type,
					totalBudget: false,
					recurring:
						subcategory.budgetSource === "currentPeriod" &&
						subcategory.isBudgetRecurring === false
							? false
							: true,
				}}
				onSubmit={handleSubmit}
				isPending={isPending}
			/>
		</>
	);
}
